/* GFI Nexus — Permission system
   ─────────────────────────────────────────────────────────────────
   Defines all granular permissions used in the shipper portal,
   grouped by menu/submenu. Each role (logistica/operacion/gerencial)
   has a default set of permissions; admin can override per user. */

export const PERMISSIONS = [
  {
    menu: { en: "Operations", es: "Operaciones" },
    submenu: { en: "Shipments", es: "Embarques", key: "shp_shipments" },
    items: [
      { key: "view_shipments",       en: "View shipments",        es: "Ver embarques" },
      { key: "create_shipments",     en: "Create shipments",      es: "Crear embarques" },
      { key: "edit_shipments",       en: "Edit shipments",        es: "Editar embarques" },
      { key: "delete_shipments",     en: "Delete shipments",      es: "Eliminar embarques" },
      { key: "send_to_groundfresh",  en: "Send to Ground Fresh",  es: "Enviar a Ground Fresh" },
      { key: "send_emails_brokers",  en: "Send emails to brokers", es: "Enviar emails a agentes aduanales" },
      { key: "upload_shipment_docs", en: "Upload shipment docs",  es: "Subir documentos de embarques" },
    ],
  },
  {
    menu: { en: "Operations", es: "Operaciones" },
    submenu: { en: "GPS Tracking", es: "Rastreo GPS", key: "shp_gps" },
    items: [
      { key: "view_gps",         en: "View GPS tracking",   es: "Ver rastreo GPS" },
      { key: "manage_gps_links", en: "Manage tracking links", es: "Gestionar enlaces" },
    ],
  },
  {
    menu: { en: "Compliance", es: "Cumplimiento" },
    submenu: { en: "Export Certificates", es: "Certificados de Exportación", key: "shp_export_certs" },
    items: [
      { key: "view_export_certs",   en: "View export certificates",   es: "Ver certificados" },
      { key: "create_export_certs", en: "Create export certificates", es: "Crear certificados" },
      { key: "edit_export_certs",   en: "Edit export certificates",   es: "Editar certificados" },
      { key: "delete_export_certs", en: "Delete export certificates", es: "Eliminar certificados" },
    ],
  },
  {
    menu: { en: "Compliance", es: "Cumplimiento" },
    submenu: { en: "Certifications", es: "Certificaciones", key: "shp_certifications" },
    items: [
      { key: "view_certifications",   en: "View certifications",   es: "Ver certificaciones" },
      { key: "create_certifications", en: "Create certifications", es: "Crear certificaciones" },
      { key: "edit_certifications",   en: "Edit certifications",   es: "Editar certificaciones" },
      { key: "delete_certifications", en: "Delete certifications", es: "Eliminar certificaciones" },
      { key: "upload_cert_docs",      en: "Upload certificate docs", es: "Subir documentos de certificados" },
    ],
  },
  {
    menu: { en: "Compliance", es: "Cumplimiento" },
    submenu: { en: "USDA Lookup", es: "Consulta USDA", key: "shp_usda_lookup" },
    items: [
      { key: "view_usda_lookup", en: "Search USDA operations", es: "Consultar operaciones USDA" },
    ],
  },
  {
    menu: { en: "Catalogs", es: "Catálogos" },
    submenu: { en: "Products", es: "Productos", key: "shp_products" },
    items: [
      { key: "view_products", en: "View products", es: "Ver productos" },
    ],
  },
  {
    menu: { en: "Catalogs", es: "Catálogos" },
    submenu: { en: "Growers", es: "Productores", key: "shp_growers" },
    items: [
      { key: "view_growers",   en: "View growers",   es: "Ver productores" },
      { key: "create_growers", en: "Create growers", es: "Crear productores" },
      { key: "edit_growers",   en: "Edit growers",   es: "Editar productores" },
      { key: "delete_growers", en: "Delete growers (if no fruit delivered)", es: "Eliminar productores (si no han entregado fruta)" },
    ],
  },
  {
    menu: { en: "Catalogs", es: "Catálogos" },
    submenu: { en: "Customs Brokers (MX)", es: "Agentes Aduanales (MX)", key: "shp_brokers" },
    items: [
      { key: "view_brokers",   en: "View MX brokers",   es: "Ver agentes aduanales MX" },
      { key: "create_brokers", en: "Create MX brokers", es: "Crear agentes aduanales MX" },
      { key: "edit_brokers",   en: "Edit MX brokers",   es: "Editar agentes aduanales MX" },
      { key: "delete_brokers", en: "Delete MX brokers", es: "Eliminar agentes aduanales MX" },
    ],
  },
  {
    menu: { en: "Settings", es: "Configuración" },
    submenu: { en: "Company Profile", es: "Perfil de Empresa", key: "shp_company" },
    items: [
      { key: "view_company_profile", en: "View company profile", es: "Ver perfil de empresa" },
    ],
  },
];

/* Flat list of all permission keys, useful for iteration. */
export const ALL_PERMISSION_KEYS = PERMISSIONS.reduce(function (acc, group) {
  group.items.forEach(function (it) { acc.push(it.key); });
  return acc;
}, []);

/* Defaults per role — what each role gets by default upon creation. */
export const ROLE_DEFAULTS = {
  logistica: {
    view_shipments: true, create_shipments: true, edit_shipments: true, delete_shipments: false,
    send_to_groundfresh: true, send_emails_brokers: true, upload_shipment_docs: true,
    view_gps: true, manage_gps_links: true,
    view_export_certs: true, create_export_certs: false, edit_export_certs: false, delete_export_certs: false,
    view_certifications: true, create_certifications: false, edit_certifications: false, delete_certifications: false, upload_cert_docs: false,
    view_usda_lookup: false,
    view_products: true,
    view_growers: true, create_growers: false, edit_growers: false, delete_growers: false,
    view_brokers: true, create_brokers: true, edit_brokers: true, delete_brokers: false,
    view_company_profile: true,
  },
  operacion: {
    view_shipments: true, create_shipments: true, edit_shipments: true, delete_shipments: true,
    send_to_groundfresh: true, send_emails_brokers: true, upload_shipment_docs: true,
    view_gps: true, manage_gps_links: true,
    view_export_certs: true, create_export_certs: true, edit_export_certs: true, delete_export_certs: true,
    view_certifications: true, create_certifications: true, edit_certifications: true, delete_certifications: true, upload_cert_docs: true,
    view_usda_lookup: true,
    view_products: true,
    view_growers: true, create_growers: true, edit_growers: true, delete_growers: true,
    view_brokers: true, create_brokers: true, edit_brokers: true, delete_brokers: true,
    view_company_profile: true,
  },
  gerencial: {
    view_shipments: true, create_shipments: false, edit_shipments: false, delete_shipments: false,
    send_to_groundfresh: false, send_emails_brokers: false, upload_shipment_docs: false,
    view_gps: true, manage_gps_links: false,
    view_export_certs: true, create_export_certs: false, edit_export_certs: false, delete_export_certs: false,
    view_certifications: true, create_certifications: false, edit_certifications: false, delete_certifications: false, upload_cert_docs: false,
    view_usda_lookup: true,
    view_products: true,
    view_growers: true, create_growers: false, edit_growers: false, delete_growers: false,
    view_brokers: true, create_brokers: false, edit_brokers: false, delete_brokers: false,
    view_company_profile: true,
  },
  compliance: {
    view_shipments: true, create_shipments: false, edit_shipments: false, delete_shipments: false,
    send_to_groundfresh: false, send_emails_brokers: false, upload_shipment_docs: true,
    view_gps: true, manage_gps_links: false,
    view_export_certs: true, create_export_certs: true, edit_export_certs: true, delete_export_certs: true,
    view_certifications: true, create_certifications: true, edit_certifications: true, delete_certifications: true, upload_cert_docs: true,
    view_usda_lookup: true,
    view_products: true,
    view_growers: true, create_growers: false, edit_growers: false, delete_growers: false,
    view_brokers: true, create_brokers: false, edit_brokers: false, delete_brokers: false,
    view_company_profile: true,
  },
};

/* Resolve the default permission map for a given role. Returns a
   fresh object (safe to mutate by the caller). */
export function defaultsForRole(role) {
  const defaults = ROLE_DEFAULTS[role] || {};
  /* Fill any missing keys with false so callers don't have to worry
     about undefined vs false. */
  const out = {};
  ALL_PERMISSION_KEYS.forEach(function (k) {
    out[k] = defaults[k] === true;
  });
  return out;
}

/* Check if a profile has a specific permission. Admin always has
   everything. Otherwise looks at profile.permissions[key]. */
export function hasPermission(profile, key) {
  if (!profile) return false;
  if (profile.role === "admin") return true;
  const perms = (profile && profile.permissions) || {};
  return perms[key] === true;
}
